import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A reusable list component for entity management
 * 
 * @param {Object} props
 * @param {Array} props.items - List of items to display
 * @param {Array} props.columns - Column definitions
 * @param {Function} props.onEdit - Function to call when edit button is clicked
 * @param {Function} props.onDelete - Function to call when delete button is clicked
 * @param {string} props.addLink - Link to add new item page
 * @param {Function} props.onAddClick - Function to call when add button is clicked
 * @param {string} props.addButtonText - Text for add button
 * @param {string} props.emptyMessage - Message to show when list is empty
 * @param {string} props.title - Title of the list
 * @param {boolean} props.isLoading - Whether the list is in loading state
 */
const EntityList = ({
  items = [],
  columns = [],
  onEdit,
  onDelete,
  addLink,
  onAddClick,
  addButtonText = 'Add New',
  emptyMessage = 'No items found',
  title = 'Entity List',
  isLoading = false
}) => {
  // Function to render cell value based on column type
  const renderCell = (item, column) => {
    const value = item[column.accessor];
    
    if (column.render) {
      return column.render(value, item);
    }
    
    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : '-';
    }
    
    if (column.type === 'number') {
      return value !== undefined && value !== null 
        ? column.format 
          ? column.format(value) 
          : value.toString()
        : '-';
    }
    
    if (column.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return value || '-';
  };

  // Render the add button either as a Link or as a button with onClick
  const renderAddButton = () => {
    if (addLink) {
      return (
        <Link to={addLink} className="btn-sm btn-primary">
          {addButtonText}
        </Link>
      );
    } else if (onAddClick) {
      return (
        <button
          onClick={onAddClick}
          className="btn-sm btn-primary"
        >
          {addButtonText}
        </button>
      );
    }
    return null;
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-4 flex justify-between items-center border-b">
        <h2 className="text-xl font-semibold">{title}</h2>
        {renderAddButton()}
      </div>
      
      {isLoading ? (
        <div className="p-6 text-center text-gray-500">Loading...</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-gray-500">
          {emptyMessage}
          <div className="mt-3">
            {addLink ? (
              <Link to={addLink} className="text-primary-600 hover:text-primary-700">
                {addButtonText}
              </Link>
            ) : onAddClick && (
              <button
                onClick={onAddClick}
                className="text-primary-600 hover:text-primary-700"
              >
                {addButtonText}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.accessor}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.header}
                  </th>
                ))}
                {(onEdit || onDelete) && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td
                      key={`${item.id}-${column.accessor}`}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                    >
                      {renderCell(item, column)}
                    </td>
                  ))}
                  {(onEdit || onDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(item)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntityList; 